import React, { useState, useEffect } from 'react';
import { employerAPI } from '../services/api';
import toast from 'react-hot-toast';
import JobPosting from './JobPosting';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Helper function to get proxied URL for job files from S3 (similar to getProxiedLogoUrl)
const getProxiedFileUrl = (fileUrl) => {
  if (!fileUrl) return null;
  
  // If it's an S3 URL, proxy it through the backend to avoid CORS issues
  if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
    // Check if it's an S3 URL
    if (fileUrl.includes('s3.amazonaws.com') || (fileUrl.includes('s3.') && fileUrl.includes('.amazonaws.com'))) {
      try {
        const url = new URL(fileUrl);
        // Extract the path (S3 key) - remove leading slash
        let s3Key = url.pathname.substring(1);
        // Remove query parameters
        s3Key = s3Key.split('?')[0];
        
        if (s3Key) {
          // Use the same proxy pattern as logos but for job files
          // Try common patterns: /jobs/files/ or /job-files/ or /files/
          return `${API_BASE_URL}/jobs/files/${s3Key}`;
        }
      } catch (e) {
        console.error('Error parsing file URL:', e);
      }
      // Fallback: return original URL
      return fileUrl;
    }
    // Not an S3 URL, return as-is (could be a CDN or other external URL)
    return fileUrl;
  }
  // Local path, prepend API base URL
  return `${API_BASE_URL}${fileUrl}`;
};

const JobList = ({ onJobUpdated, onViewMatchedCandidates }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingJob, setEditingJob] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const response = await employerAPI.getJobPostings();
      // Handle different response formats
      const jobsData = response.data?.jobs || response.data?.data?.jobs || response.data?.data || response.data || [];
      setJobs(Array.isArray(jobsData) ? jobsData : []);
      setError(null);
    } catch (err) {
      console.error('Error fetching jobs:', err);
      setError('Failed to load jobs. Please try again.');
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      // Handle ISO date strings and various formats
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'N/A';
      }
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return 'N/A';
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      published: { bg: 'bg-green-100', text: 'text-green-700', label: 'Published' },
      draft: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Draft' },
      paused: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Paused' },
      closed: { bg: 'bg-red-100', text: 'text-red-700', label: 'Closed' },
      expired: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Expired' }
    };

    const config = statusConfig[status?.toLowerCase()] || statusConfig.draft;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const handleDelete = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job posting?')) {
      return;
    }

    try {
      await employerAPI.deleteJobPosting(jobId);
      toast.success('Job posting deleted successfully');
      fetchJobs(); // Refresh the list
    } catch (err) {
      console.error('Error deleting job:', err);
      toast.error('Failed to delete job posting');
    }
  };

  if (loading) {
    return (
      <div className="ui-card p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <span className="ml-3 text-gray-600">Loading jobs...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ui-card p-6">
        <div className="text-center py-12">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={fetchJobs}
            className="btn-primary"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (jobs.length === 0) {
    return (
      <div className="ui-card p-6">
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No jobs posted</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by creating a new job posting.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ui-card">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Jobs Posted ({jobs.length})</h3>
          <button
            onClick={fetchJobs}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="divide-y divide-gray-200">
        {jobs.map((job) => (
          <div key={job.id} className="p-6 hover:bg-gray-50 transition-colors">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h4 className="text-lg font-semibold text-gray-900">{job.title}</h4>
                  {getStatusBadge(job.status)}
                  {job.isActive && (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                      Active
                    </span>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span>{job.jobType || 'Full Time'}</span>
                  </div>

                  <div className="flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span>{job.location || 'Not specified'}</span>
                  </div>

                  {job.requiredSkills && job.requiredSkills.length > 0 && (
                    <div className="flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                      <span>{job.requiredSkills.length} skill{job.requiredSkills.length !== 1 ? 's' : ''}</span>
                    </div>
                  )}

                  {(job.viewsCount && job.viewsCount > 0) && (
                    <div className="flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      <span>{job.viewsCount} views</span>
                    </div>
                  )}

                  <div className="flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>{job.applicationsCount || job.applicationCount || 0} application{(job.applicationsCount || job.applicationCount || 0) !== 1 ? 's' : ''}</span>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-gray-400">
                  <span>Posted On: {formatDate(job.publishedAt || job.published_at || job.createdAt || job.created_at)}</span>
                  {job.applicationDeadline && (
                    <span>Deadline: {formatDate(job.applicationDeadline || job.application_deadline)}</span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 ml-4">
                {onViewMatchedCandidates && (
                  <button
                    onClick={() => onViewMatchedCandidates(job.id)}
                    className="px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors font-medium"
                    title="View matched candidates for this job"
                  >
                    <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Matched Candidates
                  </button>
                )}
                <button
                  onClick={() => {
                    setEditingJob(job);
                    setShowEditModal(true);
                  }}
                  className="px-3 py-1.5 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-md transition-colors"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(job.id)}
                  className="px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>

            {job.tags && typeof job.tags === 'object' && job.tags.upload_file_url && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <a
                  href={getProxiedFileUrl(job.tags.upload_file_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1 font-medium"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  View Job Description Document
                </a>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Edit Job Modal */}
      {showEditModal && editingJob && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Edit Job Posting</h2>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingJob(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <JobPosting
                job={editingJob}
                onSuccess={(updatedJob) => {
                  toast.success('Job posting updated successfully!');
                  setShowEditModal(false);
                  setEditingJob(null);
                  fetchJobs(); // Refresh the list
                  if (onJobUpdated) {
                    onJobUpdated(updatedJob);
                  }
                }}
                onCancel={() => {
                  setShowEditModal(false);
                  setEditingJob(null);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobList;

